import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Image, Badge } from "uikit-react";
import UIkit from "uikit";
import { Link as RawLink } from "../ui-kit/Link";
import { AppContexts } from "../../providers";
import FEATURES from "../../config/features";
import feather from "feather-icons";

import Notification from "./notification";
import TrialAccount from "../common/TrialAccount";

// Helper component to render Feather icons
const FeatherIcon = ({ name, className = "" }) => {
  const icon = feather.icons[name];
  if (!icon) return null;
  
  return (
    <svg
      className={`feather-icon ${className}`}
      viewBox="0 0 24 24"
      dangerouslySetInnerHTML={{ __html: icon.contents }}
      style={{ color: "var(--dark-purple)" }}
    />
  );
};

// Pin icon with active (upright) and inactive (tilted) states
const PinIcon = ({ active }) => (
  <svg
    className="feather-icon pin-icon"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      color: active ? "var(--dark-purple)" : "currentColor",
      transform: active ? "none" : "rotate(45deg)",
      transition: "transform 0.2s ease, color 0.2s ease",
    }}
  >
    <path
      d="M12 2v8l4 2v3H8v-3l4-2V2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 15h8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M12 15v7"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const Link = ({ onClick, ...props }) => (
  <RawLink
    onClick={() => {
      onClick && onClick();
    }}
    {...props}
  />
);

const SidebarItem = ({
  to = "#",
  label,
  icon,
  avatarSrc,
  className = "",
  collapsed,
  onClick,
  badge,
  tag,
  children,
  isOpen,
  onToggle,
  isFlyoutOpen,
  onFlyoutToggle,
}) => {
  const hasChildren = !!children;

  const leftContent = (
    <span className="sidebar-link-left">
      {avatarSrc ? (
        <span className="sidebar-icon-wrap">
          <img src={avatarSrc} className="sidebar-avatar" alt={label} />
        </span>
      ) : icon ? (
        <span className="sidebar-icon-wrap">
          <FeatherIcon name={icon} />
        </span>
      ) : null}

      {!collapsed && <span className="sidebar-label">{label}</span>}
    </span>
  );

  if (hasChildren) {
    if (collapsed) {
      return (
        <li className={`sidebar-item has-children ${className} ${isFlyoutOpen ? "flyout-open" : ""}`}>
          <button
            type="button"
            className="sidebar-link sidebar-link-button"
            onClick={() => onFlyoutToggle && onFlyoutToggle()}
            uk-tooltip={label}
          >
            {leftContent}
          </button>

          {isFlyoutOpen && (
            <div className="sidebar-flyout">
              <div className="sidebar-flyout-title">{label}</div>
              <ul className="sidebar-flyout-list">
                {children.items}
              </ul>
            </div>
          )}
        </li>
      );
    }

    return (
      <li className={`sidebar-item has-children ${className}`}>
        <button
          type="button"
          className="sidebar-link sidebar-link-button"
          onClick={() => onToggle && onToggle()}
        >
          {leftContent}

          <span className={`sidebar-chevron ${isOpen ? "open" : ""}`}>
            <FeatherIcon name="chevron-down" />
          </span>
        </button>

        {isOpen && <ul className="sidebar-subnav">{children.items}</ul>}
      </li>
    );
  }

  return (
    <li className={`sidebar-item ${className}`}>
      <Link
        to={to}
        className="sidebar-link"
        onClick={onClick}
        uk-tooltip={collapsed ? label : ""}
      >
        {leftContent}

        {!collapsed && badge ? (
          <Badge className="hms-notification-count" count={badge} />
        ) : null}
        {!collapsed && tag ? (
          <span className="sidebar-tag">{tag}</span>
        ) : null}
      </Link>
    </li>
  );
};

const SidebarSubItem = ({ to, label, onClick, flyout = false }) => (
  <li className={flyout ? "sidebar-flyout-item" : "sidebar-subitem"}>
    <Link
      to={to}
      onClick={onClick}
      className={flyout ? "sidebar-flyout-link" : "sidebar-subnav-link"}
    >
      <span className={flyout ? "sidebar-flyout-label" : "sidebar-subnav-label"}>
        {label}
      </span>
    </Link>
  </li>
);

const Sidebar = () => {
  const { user: account, inbox, logout } = useContext(AppContexts.AuthContext);
  const { newOrderCount, getNewOrderCount } = useContext(
    AppContexts.OrdersContext,
  );

  const location = useLocation();
  const [pinned, setPinned] = useState(() => localStorage.getItem("sidebar-pinned") === "true");
  const [collapsed, setCollapsed] = useState(() => !pinned);
  const [openMenu, setOpenMenu] = useState(null);
  const [flyoutMenu, setFlyoutMenu] = useState(null);
  const [mobileOpenMenu, setMobileOpenMenu] = useState(null);

    useEffect(() => {
        getNewOrderCount();
    }, []);

    useEffect(() => {
        if (!pinned) {
            setCollapsed(true);
        }
        setOpenMenu(null);
        setFlyoutMenu(null);
        setMobileOpenMenu(null);
        const mobileMenu = document.getElementById('mobile-menu');
        if (mobileMenu) {
            UIkit.offcanvas(mobileMenu).hide();
        }
    }, [location.pathname]);

  const handleTogglePin = () => {
    const next = !pinned;
    setPinned(next);
    localStorage.setItem("sidebar-pinned", String(next));
    if (next) {
      setCollapsed(false);
    }
  };

  const handleLogout = async () => {
    logout()
      .then(() => {
        console.log("Goodbye");
      })
      .catch((err) => {
        console.warn("Error logging out:", err);
      });
  };

  const profileImage = useMemo(() => {
    return (
      (account?.user?.attachments || []).find((a) => a.type === "image") || null
    );
  }, [account]);

  const isActive = (paths) => {
    if (!Array.isArray(paths)) paths = [paths];

    return paths.some((path) => {
      const regex = new RegExp(`^${path}`);
      return regex.test(location.pathname);
    })
      ? "is-active"
      : "";
  };

  const hasExtras = () => {
    return (account?.details?.product || "").split(",").includes("extras");
  };

  const hasFlag = (flagName) => {
    const accountFlags = account?.flags || [];
    return accountFlags.some((f) => f.name === flagName);
  };

  if (!account) return null;

  return (
    <>
      <div className={`rbr-sidebar ${collapsed ? "collapsed" : ""} ${pinned ? "pinned" : ""}`}>
        <div className="rbr-sidebar-top">
          <div className="rbr-sidebar-brand">
            <Link to="/" className="rbr-sidebar-logo">
              <Image src="/images/ruebaur-logo.png" alt="RueBaRue" />
            </Link>

            <div className="rbr-sidebar-actions">
              {!collapsed && (
                <button
                  type="button"
                  className={`rbr-sidebar-pin ${pinned ? "is-pinned" : ""}`}
                  onClick={handleTogglePin}
                  title=""
                  uk-tooltip={`title: ${pinned ? "Unpin Sidebar" : "Pin Sidebar Open"}; pos: right; delay: 300`}
                >
                  <PinIcon active={pinned} />
                </button>
              )}
              <button
                type="button"
                className="rbr-sidebar-toggle"
                onClick={() => setCollapsed((prev) => !prev)}
                title={collapsed ? "Expand" : "Collapse"}
              >
                <FeatherIcon name={collapsed ? "menu" : "sidebar"} />
              </button>
            </div>
          </div>

          {!!account && account?.details?.type !== "onboarding" && (
            <ul className="sidebar-nav">
                <SidebarItem
                to="/messages"
                label="Messages"
                icon="message-circle"
                className={isActive("/messages")}
                collapsed={collapsed}
                />
                <SidebarItem
                to="/guests"
                label="Guests"
                icon="users"
                className={isActive("/guests")}
                collapsed={collapsed}
                />
                <SidebarItem
                to="/contacts"
                label="Contacts"
                icon="user"
                className={isActive("/contacts")}
                collapsed={collapsed}
                />
                {hasFlag("crm") && (
                <SidebarItem
                to="/crm"
                label="CRM"
                icon="briefcase"
                className={isActive("/crm")}
                collapsed={collapsed}
                />
                )}
                <SidebarItem
                to="/smart-locks"
                label="Smart Locks"
                icon="key"
                className={isActive("/smart-locks")}
                collapsed={collapsed}
                tag="New"
                />
                {hasExtras() && (
                <SidebarItem
                    to="/orders"
                    label="Orders"
                    icon="shopping-cart"
                    className={isActive("/orders")}
                    collapsed={collapsed}
                    badge={+newOrderCount > 0 ? +newOrderCount : null}
                />
                )}

                <li className="sidebar-divider"></li>

                <SidebarItem
                    label="Messaging"
                    icon="zap"
                    className={isActive([
                        "/scheduler",
                        "/extend-guest-stay",
                        "/alerts",
                        "/surveys",
                        "/saved-responses",
                        "/message-templates",
                    ])}
                    collapsed={collapsed}
                    isOpen={openMenu === "messaging"}
                    onToggle={() =>
                        setOpenMenu((prev) => (prev === "messaging" ? null : "messaging"))
                    }
                    isFlyoutOpen={flyoutMenu === "messaging"}
                    onFlyoutToggle={() => {
                        if (collapsed) {
                            setCollapsed(false);
                        }
                        setOpenMenu((prev) => (prev === "messaging" ? null : "messaging"));
                        setFlyoutMenu(null);
                    }}
                    children={{
                        items: (
                        <>
                            <SidebarSubItem to="/scheduler" label="Scheduler" flyout={collapsed} />
                            <SidebarSubItem to="/extend-guest-stay" label="Extend Stays" flyout={collapsed} />
                            <SidebarSubItem to="/alerts" label="Alerts" flyout={collapsed} />
                            <SidebarSubItem to="/surveys" label="Surveys" flyout={collapsed} />
                            <SidebarSubItem to="/saved-responses" label="Saved Responses" flyout={collapsed} />
                            <SidebarSubItem to="/message-templates" label="Message Templates" flyout={collapsed} />
                        </>
                        ),
                    }}
                    />

                <SidebarItem
                    label="Operations"
                    icon="sliders"
                    className={isActive([
                        "/operations",
                        "/operations/calender",
                        "/operations/housekeeping",
                        "/operations/inspection-board",
                        "/operations/maintenance",
                        "/operations/rules",
                        "/operations/storage/locations",
                        "/operations/storage/items",
                        "/operations/storage/stock",
                        "/operations/storage/movements",
                        "/operations/home-inventory",
                        "/operations/standards-structure",
                        "/operations/property-setup",
                        "/operations/checklists",
                        "/work-orders",
                        "/work-schedules",
                        "/availability-calendar",
                    ])}
                    collapsed={collapsed}
                    isOpen={openMenu === "operations"}
                    onToggle={() =>
                        setOpenMenu((prev) => (prev === "operations" ? null : "operations"))
                    }
                    isFlyoutOpen={flyoutMenu === "operations"}
                    onFlyoutToggle={() => {
                        if (collapsed) {
                            setCollapsed(false);
                        }
                        setOpenMenu((prev) => (prev === "operations" ? null : "operations"));
                        setFlyoutMenu(null);
                    }}
                    children={{
                        items: (
                        <>
                            <SidebarSubItem to="/operations" label="Dashboard" flyout={collapsed} />
                            {FEATURES.CALENDAR_ENABLED && (
                            <SidebarSubItem
                                to="/operations/calender"
                                label="Calendar"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.HOUSEKEEPING_ENABLED && (
                            <SidebarSubItem
                                to="/operations/housekeeping"
                                label="Housekeeping"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.INSPECTIONS_ENABLED && (
                            <SidebarSubItem
                                to="/operations/inspection-board"
                                label="Inspection Board"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.MAINTENANCE_ENABLED && (
                            <SidebarSubItem
                                to="/operations/maintenance"
                                label="Maintenance"
                                flyout={collapsed}
                            />
                            )}
                            <SidebarSubItem
                            to="/work-orders"
                            label="Work Orders"
                            flyout={collapsed}
                            />
                            {FEATURES.RULES_ENABLED && (
                            <SidebarSubItem
                                to="/operations/rules"
                                label="Rules"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.STORAGE_LOCATIONS_ENABLED && (
                            <SidebarSubItem
                                to="/operations/storage/locations"
                                label="Storage Locations"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.STORAGE_ITEMS_ENABLED && (
                            <SidebarSubItem
                                to="/operations/storage/items"
                                label="Storage Items"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.MANAGE_STOCK_ENABLED && (
                            <SidebarSubItem
                                to="/operations/storage/stock"
                                label="Manage Stock"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.STOCK_MOVEMENTS_ENABLED && (
                            <SidebarSubItem
                                to="/operations/storage/movements"
                                label="Stock Movements"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.HOME_INVENTORY_ENABLED && (
                            <SidebarSubItem
                                to="/operations/home-inventory"
                                label="Home Inventory"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.CLEANING_STANDARDS_ENABLED && (
                            <SidebarSubItem
                                to="/operations/standards-structure"
                                label="Cleaning & Inspection Standards"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.PROPERTY_SETUP_ENABLED && (
                            <SidebarSubItem
                                to="/operations/property-setup"
                                label="Property Setup"
                                flyout={collapsed}
                            />
                            )}
                            {FEATURES.CHECKLISTS_ENABLED && (
                            <SidebarSubItem
                                to="/operations/checklists"
                                label="Checklists"
                                flyout={collapsed}
                            />
                            )}
                            <SidebarSubItem
                            to="/work-schedules"
                            label="Work Schedules"
                            flyout={collapsed}
                            />
                            <SidebarSubItem
                            to="/availability-calendar"
                            label="Availability Calendar"
                            flyout={collapsed}
                            />
                        </>
                        ),
                    }}
                    />

                <SidebarItem
                    label="Guest Guides"
                    icon="book"
                    className={isActive([
                        "/master-home-guide",
                        "/properties",
                        "/extras",
                        "/areas",
                        "/property-field-values",
                        "/subscriptions",
                    ])}
                    collapsed={collapsed}
                    isOpen={openMenu === "guides"}
                    onToggle={() =>
                        setOpenMenu((prev) => (prev === "guides" ? null : "guides"))
                    }
                    isFlyoutOpen={flyoutMenu === "guides"}
                    onFlyoutToggle={() => {
                        if (collapsed) {
                            setCollapsed(false);
                        }
                        setOpenMenu((prev) => (prev === "guides" ? null : "guides"));
                        setFlyoutMenu(null);
                    }}
                    children={{
                        items: (
                        <>
                            <SidebarSubItem
                            to="/master-home-guide"
                            label="Master Home Guide"
                            flyout={collapsed}
                            />
                            <SidebarSubItem
                            to="/properties"
                            label="Home Guides"
                            flyout={collapsed}
                            />
                            {hasExtras() && (
                            <SidebarSubItem
                                to="/extras"
                                label="Extras Guide"
                                flyout={collapsed}
                            />
                            )}
                            <SidebarSubItem
                            to="/areas"
                            label="Area Guides"
                            flyout={collapsed}
                            />
                            <SidebarSubItem
                            to="/property-field-values"
                            label="Property Field Values"
                            flyout={collapsed}
                            />
                            <SidebarSubItem
                            to="/subscriptions"
                            label="Subscriptions"
                            flyout={collapsed}
                            />
                        </>
                        ),
                    }}
                    />

                <li className="sidebar-divider"></li>

                <SidebarItem
                to="/tasks"
                label="My Tasks"
                icon="check-square"
                className={isActive("/tasks")}
                collapsed={collapsed}
                />

                <SidebarItem
                    to="/reports"
                    label="Reports"
                    icon="bar-chart-2"
                    className={isActive("/reports")}
                    collapsed={collapsed}
                    />

            </ul>
            )}
        </div>

       {!!account && (
        <div className="rbr-sidebar-bottom">
          <div className="sidebar-account-info">
            <div className="sidebar-account-info-row">
              <span className="sidebar-account-info-label">Signed in as</span>
              <span className="sidebar-account-info-value" title={account?.user?.email}>{account?.user?.email}</span>
            </div>
            {inbox && inbox.phone && (
              <div className="sidebar-account-info-row">
                <span className="sidebar-account-info-label">SMS Number</span>
                <span className="sidebar-account-info-value">{inbox.phone}</span>
              </div>
            )}
          </div>
        </div>
        )}

      </div>

      <button
        id="sidebar-mobile-toggle"
        className="uk-hidden@xl rbr-mobile-toggle"
        uk-toggle="target: #mobile-menu; animation: uk-animation-fade"
        aria-expanded="false"
      >
        <span uk-navbar-toggle-icon=""></span>
      </button>

      <div id="mobile-menu" uk-offcanvas="overlay: true">
        <div className="uk-offcanvas-bar mobile-sidebar">
          <div className="mobile-sidebar-header">
            <Link to="/" className="rbr-sidebar-logo">
              <Image src="/images/ruebaur-logo.png" alt="RueBaRue" />
            </Link>
            <button
              className="uk-offcanvas-close mobile-sidebar-close"
              type="button"
              uk-close
            >
              <Image src="/images/close-small.svg" alt="close" />
            </button>
          </div>

          {!!account && (
            <div className="mobile-menu-profile">
              <Link to="/profile" className="mobile-menu-profile-link">
                <img
                  src={profileImage?.url || "/images/profile.svg"}
                  className="mobile-menu-avatar"
                  alt="Profile"
                />
                <div className="mobile-menu-profile-info">
                  <span className="mobile-menu-profile-name">
                    {account?.user?.first_name || account?.user?.email}
                  </span>
                  <span className="mobile-menu-profile-email">
                    {account?.user?.email}
                  </span>
                </div>
              </Link>
            </div>
          )}

          {!!account && account?.details?.type !== "onboarding" && (
          <ul className="sidebar-nav">
            <SidebarItem to="/messages" label="Messages" icon="message-circle" className={isActive("/messages")} collapsed={false} />
            <SidebarItem to="/guests" label="Guests" icon="users" className={isActive("/guests")} collapsed={false} />
            <SidebarItem to="/contacts" label="Contacts" icon="user" className={isActive("/contacts")} collapsed={false} />
            {hasFlag("crm") && (
              <SidebarItem to="/crm" label="CRM" icon="briefcase" className={isActive("/crm")} collapsed={false} />
            )}
            <SidebarItem to="/smart-locks" label="Smart Locks" icon="key" className={isActive("/smart-locks")} collapsed={false} />
            {hasExtras() && (
              <SidebarItem to="/orders" label="Orders" icon="shopping-cart" className={isActive("/orders")} collapsed={false} badge={+newOrderCount > 0 ? +newOrderCount : null} />
            )}

            <li className="sidebar-divider"></li>

            <SidebarItem
              label="Messaging"
              icon="zap"
              className={isActive(["/scheduler", "/extend-guest-stay", "/alerts", "/surveys", "/saved-responses", "/message-templates"])}
              collapsed={false}
              isOpen={mobileOpenMenu === "messaging"}
              onToggle={() => setMobileOpenMenu((prev) => (prev === "messaging" ? null : "messaging"))}
              children={{
                items: (
                  <>
                    <SidebarSubItem to="/scheduler" label="Scheduler" />
                    <SidebarSubItem to="/extend-guest-stay" label="Extend Stays" />
                    <SidebarSubItem to="/alerts" label="Alerts" />
                    <SidebarSubItem to="/surveys" label="Surveys" />
                    <SidebarSubItem to="/saved-responses" label="Saved Responses" />
                    <SidebarSubItem to="/message-templates" label="Message Templates" />
                  </>
                ),
              }}
            />

            <SidebarItem
              label="Operations"
              icon="sliders"
              className={isActive(["/operations", "/work-orders", "/work-schedules", "/availability-calendar"])}
              collapsed={false}
              isOpen={mobileOpenMenu === "operations"}
              onToggle={() => setMobileOpenMenu((prev) => (prev === "operations" ? null : "operations"))}
              children={{
                items: (
                  <>
                    <SidebarSubItem to="/operations" label="Dashboard" />
                    {FEATURES.CALENDAR_ENABLED && <SidebarSubItem to="/operations/calender" label="Calendar" />}
                    {FEATURES.HOUSEKEEPING_ENABLED && <SidebarSubItem to="/operations/housekeeping" label="Housekeeping" />}
                    {FEATURES.INSPECTIONS_ENABLED && <SidebarSubItem to="/operations/inspection-board" label="Inspection Board" />}
                    {FEATURES.MAINTENANCE_ENABLED && <SidebarSubItem to="/operations/maintenance" label="Maintenance" />}
                    <SidebarSubItem to="/work-orders" label="Work Orders" />
                    {FEATURES.RULES_ENABLED && <SidebarSubItem to="/operations/rules" label="Rules" />}
                    {FEATURES.STORAGE_LOCATIONS_ENABLED && <SidebarSubItem to="/operations/storage/locations" label="Storage Locations" />}
                    {FEATURES.STORAGE_ITEMS_ENABLED && <SidebarSubItem to="/operations/storage/items" label="Storage Items" />}
                    {FEATURES.MANAGE_STOCK_ENABLED && <SidebarSubItem to="/operations/storage/stock" label="Manage Stock" />}
                    {FEATURES.STOCK_MOVEMENTS_ENABLED && <SidebarSubItem to="/operations/storage/movements" label="Stock Movements" />}
                    {FEATURES.HOME_INVENTORY_ENABLED && <SidebarSubItem to="/operations/home-inventory" label="Home Inventory" />}
                    {FEATURES.CLEANING_STANDARDS_ENABLED && <SidebarSubItem to="/operations/standards-structure" label="Cleaning & Inspection Standards" />}
                    {FEATURES.PROPERTY_SETUP_ENABLED && <SidebarSubItem to="/operations/property-setup" label="Property Setup" />}
                    {FEATURES.CHECKLISTS_ENABLED && <SidebarSubItem to="/operations/checklists" label="Checklists" />}
                    <SidebarSubItem to="/work-schedules" label="Work Schedules" />
                    <SidebarSubItem to="/availability-calendar" label="Availability Calendar" />
                  </>
                ),
              }}
            />

            <SidebarItem
              label="Guest Guides"
              icon="book"
              className={isActive(["/master-home-guide", "/properties", "/extras", "/areas", "/property-field-values", "/subscriptions"])}
              collapsed={false}
              isOpen={mobileOpenMenu === "guides"}
              onToggle={() => setMobileOpenMenu((prev) => (prev === "guides" ? null : "guides"))}
              children={{
                items: (
                  <>
                    <SidebarSubItem to="/master-home-guide" label="Master Home Guide" />
                    <SidebarSubItem to="/properties" label="Home Guides" />
                    {hasExtras() && <SidebarSubItem to="/extras" label="Extras Guide" />}
                    <SidebarSubItem to="/areas" label="Area Guides" />
                    <SidebarSubItem to="/property-field-values" label="Property Field Values" />
                    <SidebarSubItem to="/subscriptions" label="Subscriptions" />
                  </>
                ),
              }}
            />

            <li className="sidebar-divider"></li>

            <SidebarItem to="/tasks" label="My Tasks" icon="check-square" className={isActive("/tasks")} collapsed={false} />
            <SidebarItem to="/reports" label="Reports" icon="bar-chart-2" className={isActive("/reports")} collapsed={false} />

            <li className="sidebar-divider"></li>

            <SidebarItem to="/profile" label="Profile" icon="user" className={isActive("/profile")} collapsed={false} />
            <SidebarItem
              to="#"
              label="Knowledge Base"
              icon="info"
              collapsed={false}
              onClick={() => window.open("https://intercom.help/ruebarueapp/en/", "_blank")}
            />
            <SidebarItem to="#" label="Sign Out" icon="log-out" collapsed={false} onClick={handleLogout} />
          </ul>
          )}

          <div className="sidebar-account-info" style={{ padding: '16px 12px', borderTop: '1px solid #e5e7eb', marginTop: 'auto' }}>
            <div className="sidebar-account-info-row">
              <span className="sidebar-account-info-label">Signed in as</span>
              <span className="sidebar-account-info-value">{account?.user?.email}</span>
            </div>
            {inbox && inbox.phone && (
              <div className="sidebar-account-info-row">
                <span className="sidebar-account-info-label">SMS Number</span>
                <span className="sidebar-account-info-value">{inbox.phone}</span>
              </div>
            )}
          </div>

        </div>
      </div>

      <TrialAccount />
      <Notification />
    </>
  );
};

export default Sidebar;